import React, { useState, useEffect } from 'react';
import { Canvas } from '@react-three/fiber';
import { OrbitControls } from '@react-three/drei';
import './LaptopViewer.css';
import 'bootstrap/dist/css/bootstrap.min.css';
import { Modal, Button, Form } from 'react-bootstrap';
import MacModel from '../components/MacModel';
import { FaWhatsapp } from "react-icons/fa";
import logo from '../assets/TT logo.png';
import "driver.js/dist/driver.css";
import { driver } from "driver.js";
import { Link } from 'react-router-dom';

// Map raw model part names → user-friendly aliases
const partNameAliases = {
  "Track_pad_-_1": "Trackpad",
  "Power_ON_button_-_2": "Power Button",
  "Keys_&_keypad_-3": "Keyboard",
  "Speaker_-_4": "Speaker",
  "Display_Screen_-_5": "Display Screen",
  "Camera_-_6": "Camera",
  "Display_panel_-_7": "Display Panel",
  "Keypad_Backlight_-_8": "Keyboard Backlight",
  "Power_ON_&_Touch_ID_-_9": "Power & Touch ID",
  "Key_pad_panel_-_10": "Keyboard Panel",
  "Fan_-_11": "Fan",
  "Port_-_12": "Port (Left)",
  "Charging_,HDMI_Port_Right_-13": "Charging & HDMI Port (Right)",
  "Audio_Out_-_14": "Audio Out"
};

// Issues mapped to ALIAS (not raw names)
const partIssues = {
  "Display Screen": [
    "Screen flickers",
    "LCD broken",
    "No display on boot",
    "Black screen randomly",
    "Dim screen at full brightness",
    "Intermittent display",
    "Lines across the screen",
    "Screen turns off during use",
    "Cracked or broken screen",
    "Colour distortion or flicker",
    "Screen ghosting on movement"
  ],
  "Charging & HDMI Port (Right)": [
    "Laptop not charging",
    "Charger not recognized",
    "Loose charging port & broken",
    "Charging only at a certain angle",
    "Power adapter overheating",
    "USB-C charging not working",
    "Slow charging",
    "No charging light indicator",
    "Charging stops randomly"
  ],
  "Port (Left)": [
    "Laptop not charging",
    "Loose or broken port",
    "USB-C not recognized"
  ],
  "Trackpad": [
    "Trackpad unresponsive",
    "Cursor jumping or lagging",
    "Trackpad click not working"
  ],
  "Keyboard": [
    "Some keys not working",
    "Entire keyboard dead",
    "Double typing or stuck keys",
    "Keyboard damage"
  ],
  "Keyboard Backlight": [
    "Backlight not turning on",
    "Uneven brightness"
  ],
  "Keyboard Panel": [
    "Loose fitting",
    "Panel cracked"
  ],
  "Power Button": [
    "Button stuck",
    "Not responsive"
  ],
  "Power & Touch ID": [
    "Touch ID not working",
    "Not detecting fingerprint"
  ],
  "Speaker": [
    "No sound from speakers",
    "Distorted or crackling audio",
    "Low volume"
  ],
  "Camera": [
    "Webcam not turning on",
    "Black screen on webcam",
    "Camera flickers or lags"
  ],
  "Audio Out": [
    "Mic not working",
    "Headphones not detected",
    "Audio jack loose or broken"
  ],
  "Fan": [
    "Laptop gets too hot",
    "Fans running loud constantly",
    "Fan not spinning",
    "Fan error"
  ],
  // General issues that are motherboard/logic-board related
  "Motherboard": [
    "Laptop not powering on",
    "No POST or beeps",
    "Liquid spill on board",
    "Short circuit signs",
    "Burnt smell or heat spots",
    "Faulty IC",
    "USB ports not working",
    "Thunderbolt port failure",
    "No external monitor detection",
    "Error messages"
  ],
  "Battery": [
    "Battery drains fast",
    "Battery not charging fully",
    "Laptop shuts down on battery",
    "Battery stuck at 0% or 100%",
    "Battery not detected",
    "Swollen battery",
    "Wrong battery level shown",
    "Battery heats up during use"
  ],
  "Storage": [
    "SSD/HDD not detected",
    "Stuck on logo screen",
    "Boot device not found",
    "Frequent BSODs",
    "Slow boot time"
  ]
};


const MacBook = () => {
  const [selectedPart, setSelectedPart] = useState(null); // RAW name
  const [hoveredPart, setHoveredPart] = useState(null);   // RAW name
  const [modalShow, setModalShow] = useState(false);
  const [modalContent, setModalContent] = useState('');
  const [formData, setFormData] = useState({ name: '', email: '', phone: '' });

  // Convert raw name → alias for UI
  const getAlias = (rawName) => partNameAliases[rawName] || rawName;

  const hoveredAlias = getAlias(hoveredPart);
  const selectedAlias = getAlias(selectedPart);

  const initialCameraPosition = [0, 60, 60];

  // Store RAW name for highlight logic
  const handlePartClick = (rawName) => {
    setSelectedPart(rawName);
  };

  const handleIssueClick = (issue) => {
    setModalContent(issue);
    setModalShow(true);
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleSubmit = () => {
    const message = `Hello, I want to report an issue:
- Device: Nintendo Switch
- Part Name: ${selectedAlias || "Not Selected"}
- Issue: ${modalContent}
- Name: ${formData.name}
- Email: ${formData.email}
- Phone: ${formData.phone}
- Location: ${formData.location}
- Issue Description: ${formData.issue}`;

    const encodedMessage = encodeURIComponent(message);
    const whatsappNumber = "971567756193"; // ✅ replace with your WhatsApp number

    // Works on both WhatsApp mobile app & WhatsApp Web (desktop)
    const url = `https://api.whatsapp.com/send?phone=${whatsappNumber}&text=${encodedMessage}`;

    window.open(url, "_blank");

    setModalShow(false);
    setFormData({ name: "", email: "", phone: "" });
  };

  // ✅ Guided tour with driver.js
  const startTour = () => {
    const tourDriver = driver({
      showProgress: true,
      showButtons: ["next", "previous", "close"],
      steps: [
        {
          element: ".model-canvas",
          popover: {
            title: "3D Laptop Model",
            description: "👉 Rotate and zoom the product with your mouse or touchpad.",
          },
        },
        {
          element: ".sidebar",
          popover: {
            title: "Parts List",
            description: "Click a part from this list or directly on the product model.",
          },
        },
        {
          element: ".custom-modal",
          popover: {
            title: "Submit an Issue",
            description: "Choose an issue and send it via WhatsApp for quick support!",
          },
        },
      ],
    });

    tourDriver.drive();
  };

  useEffect(() => {
    const hasSeenTour = localStorage.getItem("hasSeenTour");
    if (!hasSeenTour) {
      startTour();
      localStorage.setItem("hasSeenTour", "true");
    }
  }, []);


  return (
    <>
    <header className="top-header d-flex justify-content-between align-items-center px-3 py-2">
                <Link to={'/'}><img src={logo} style={{height: '160px', margin: '-40px -30px', padding: '0px'}} /></Link>
                <Button variant="info" onClick={startTour}>
                  📖 Show Instructions
                </Button>
              </header>
      <div className="laptop-viewer-container">
        <div className="model-canvas">
          <Canvas camera={{ position: initialCameraPosition, fov: 50 }}>
            <ambientLight />
            <directionalLight position={[10, 10, 5]} />
            <MacModel
              onPartClick={handlePartClick}
              onHover={setHoveredPart}
              selectedPart={selectedPart} // RAW name
            />
            <OrbitControls />
          </Canvas>
          {hoveredPart && <div className="tooltip">{hoveredAlias}</div>}
        </div>

        <div className="sidebar">
          {!selectedPart && (
            <>
              <h3>All Parts</h3>
              <ul>
                {Object.entries(partNameAliases).map(([rawName, alias], i) => (
                  <li
                    key={i}
                    onClick={() => handlePartClick(rawName)} // RAW name
                    style={{ cursor: 'pointer' }}
                  >
                    {alias}
                  </li>
                ))}
              </ul>
            </>
          )}

          {selectedPart && (
            <>
              <h3>{selectedAlias} Issues</h3>
              <ul>
                {(partIssues[selectedAlias] || []).map((issue, i) => (
                  <li
                    key={i}
                    onClick={() => handleIssueClick(issue)}
                    style={{ cursor: 'pointer' }}
                  >
                    {issue}
                  </li>
                ))}
              </ul>
              <Button
                variant="secondary"
                size="sm"
                onClick={() => setSelectedPart(null)}
              >
                🔙 Back to Parts
              </Button>
            </>
          )}
        </div>
      </div>

      <Modal show={modalShow} onHide={() => setModalShow(false)} centered className="custom-modal">
        <Modal.Header style={{ color: 'white' }} closeButton>
          <Modal.Title>{modalContent}</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Form>
            <Form.Group className="mb-3" controlId="formName">
              <Form.Label>Name</Form.Label>
              <Form.Control
                type="text"
                placeholder="Enter your name"
                name="name"
                value={formData.name}
                onChange={handleInputChange}
              />
            </Form.Group>
            <Form.Group className="mb-3" controlId="formEmail">
              <Form.Label>Email</Form.Label>
              <Form.Control
                type="email"
                placeholder="Enter email"
                name="email"
                value={formData.email}
                onChange={handleInputChange}
              />
            </Form.Group>
            <Form.Group className="mb-3" controlId="formPhone">
              <Form.Label>Phone</Form.Label>
              <Form.Control
                type="tel"
                placeholder="Enter phone number"
                name="phone"
                value={formData.phone}
                onChange={handleInputChange}
              />
            </Form.Group>
            <Form.Group className="mb-3" controlId="formLocation">
              <Form.Label>Location</Form.Label>
              <Form.Control
                type="text"
                placeholder="Enter Location"
                name="location"
                value={formData.location}
                onChange={handleInputChange}
              />
            </Form.Group>
            <Form.Group className="mb-3" controlId="formEmergency">
              <Form.Label>Location</Form.Label>
              <Form.Control
                type="text"
                placeholder="Urgency Level"
                name="Emergency"
                value={formData.Emergency}
                onChange={handleInputChange}
              />
            </Form.Group>
            <Form.Group className="mb-3" controlId="formIssue">
              <Form.Label>Describe Issue</Form.Label>
              <Form.Control
                as="textarea"
                rows={4} // You can adjust the number of rows as needed
                placeholder="Enter Issue Description"
                name="issue"
                value={formData.issue}
                onChange={handleInputChange}
              />
            </Form.Group>

          </Form>
          <Button variant="success" onClick={handleSubmit} className="d-flex align-items-center gap-2">
            <FaWhatsapp size={20} />
            Submit Issue on WhatsApp
          </Button>

        </Modal.Body>
        <Modal.Footer>

        </Modal.Footer>
      </Modal>
    </>
  );
};

export default MacBook;
